/**
 * TrendIndicator Component
 * Displays percentage change with colored arrow indicator
 */

import React from 'react';
import { Box, Typography } from '@mui/material';
import {
  TrendingUp,
  TrendingDown,
  TrendingFlat
} from '@mui/icons-material';

export interface TrendIndicatorProps {
  value: number; // Percentage change (e.g., 2.5 for +2.5%, -3.2 for -3.2%)
  showIcon?: boolean;
  size?: 'small' | 'medium' | 'large';
  inverse?: boolean; // If true, negative trend is shown as positive (e.g., for costs)
}

const TrendIndicator: React.FC<TrendIndicatorProps> = ({
  value,
  showIcon = true,
  size = 'small',
  inverse = false
}) => {
  // Determine trend direction
  const isPositive = inverse ? value < 0 : value > 0;
  const isNegative = inverse ? value > 0 : value < 0;
  const isNeutral = value === 0;

  // Color selection
  const color = isPositive ? '#2e7d32' : isNegative ? '#d32f2f' : '#757575';

  // Icon selection
  const Icon = isPositive ? TrendingUp : isNegative ? TrendingDown : TrendingFlat;

  // Size mapping
  const iconSizeMap = {
    small: 16,
    medium: 20,
    large: 24
  };

  const fontSizeMap = {
    small: '0.75rem',
    medium: '0.875rem',
    large: '1rem'
  };

  return (
    <Box
      display="flex"
      alignItems="center"
      gap={0.5}
      sx={{ color }}
    >
      {showIcon && (
        <Icon sx={{ fontSize: iconSizeMap[size] }} />
      )}
      <Typography
        variant="body2"
        sx={{
          fontSize: fontSizeMap[size],
          fontWeight: 600,
          color
        }}
      >
        {value > 0 ? '+' : ''}{value.toFixed(1)}%
      </Typography>
    </Box>
  );
};

export default TrendIndicator;
